import React, { useState, useEffect } from 'react';
import { FaStar, FaQuoteLeft, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { fetchAPI } from '../../utils/api';
import { useNavigate } from 'react-router-dom';

export default function TestimonialsSection() {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const loadTestimonials = async () => {
            try {
                const data = await fetchAPI('/public/get_feedback.php');
                // Take only the first 3 items for the home page
                setTestimonials(data.slice(0, 3));
            } catch (err) {
                console.error('Failed to load testimonials:', err);
            } finally {
                setLoading(false);
            }
        };
        loadTestimonials();
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 30, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100
            }
        }
    };

    return (
        <section className="py-20 bg-gray-50">
            <div className="max-w-6xl mx-auto px-6">
                {/* Modern Header */}
                <div className="text-center mb-16">
                    <div className="inline-block mb-6">
                        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-2xl px-6 py-3 shadow-sm">
                            <FaQuoteLeft className="text-blue-600 text-lg" />
                            <span className="text-blue-700 font-semibold text-sm lemon-milk">STUDENT SUCCESS STORIES</span>
                        </div>
                    </div>

                    <h2 className="text-4xl font-black text-gray-900 mb-4 lemon-milk">
                        Student{' '}
                        <span className="text-blue-600">
                            Feedback
                        </span>
                    </h2>

                    <p className="text-lg text-gray-600 max-w-2xl mx-auto poppins">
                        Discover how our students achieved their academic goals and built confidence
                        in mathematics through our proven teaching methods.
                    </p>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <FaSpinner className="animate-spin text-blue-600 text-3xl" />
                    </div>
                ) : (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="grid md:grid-cols-3 gap-6"
                    >
                        {testimonials.map((testimonial, index) => (
                            <motion.div
                                key={testimonial.id || index}
                                variants={itemVariants}
                                className="group bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 as"
                            >
                                <div className="flex items-center gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <FaStar key={i} className="text-yellow-500 text-sm" />
                                    ))}
                                </div>

                                <div className="mb-6">
                                    <FaQuoteLeft className="text-blue-600 text-lg mb-3 opacity-60" />
                                    <p className="text-gray-700 text-sm leading-relaxed poppins line-clamp-4">
                                        {testimonial.content}
                                    </p>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-blue-600/20 rounded-full blur-sm"></div>
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.author)}&background=random`}
                                            alt={testimonial.author}
                                            className="relative w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                                        />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900 text-sm lemon-milk">{testimonial.author}</h4>
                                        <p className="text-blue-600 font-medium text-xs poppins">
                                            {testimonial.tags && testimonial.tags[0] ? testimonial.tags[0] : 'Student'}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}

                {/* Call to Action */}
                <div className="mt-16 text-center">
                    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 lemon-milk">Ready to Join Our Success Stories?</h3>
                        <p className="text-gray-600 mb-6 poppins">Start your mathematics journey today and become our next success story.</p>
                        <button
                            onClick={() => navigate('/feedback')}
                            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors lemon-milk"
                        >
                            View All Stories
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
